import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, ExternalLink } from 'lucide-react';

export default function DatenschutzPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="UniSide Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-bold font-outfit">UniSide</span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card className="border-slate-100 shadow-sm rounded-2xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              <CardTitle className="text-3xl font-outfit">Datenschutzerklärung</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <p className="text-slate-600 mb-6">
              <strong>Stand: Februar 2026</strong>
            </p>

            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <h4 className="text-green-800 font-semibold mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Unser Datenschutz-Versprechen
              </h4>
              <p className="text-green-700 text-sm mb-0">
                UniSide verwendet <strong>keine Tracking-Cookies</strong>, <strong>keine Analyse-Tools</strong> und 
                <strong> keine Werbung</strong>. Ihre Daten werden ausschließlich für die Jobvermittlung verwendet 
                und niemals an Dritte zu Werbezwecken verkauft.
              </p>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3">1. Verantwortlicher</h3>
            <p>
              Verantwortlich für die Datenverarbeitung auf dieser Website ist:
            </p>
            <p>
              <strong>Verein ZORO</strong><br />
              Liese-Prokop-Gasse 7/1<br />
              3452 Heiligeneich, Österreich<br />
              E-Mail: datenschutz@uniside.at<br />
              Telefon: +43/699 17070547
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">2. Welche personenbezogenen Daten erfassen wir?</h3>
            
            <h4 className="text-lg font-semibold mt-4 mb-2">2.1 Bei der Registrierung</h4>
            <table className="w-full border-collapse border border-slate-300 my-4">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-300 p-2 text-left">Datenkategorie</th>
                  <th className="border border-slate-300 p-2 text-left">Zweck</th>
                  <th className="border border-slate-300 p-2 text-left">Rechtsgrundlage</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-300 p-2">E-Mail-Adresse</td>
                  <td className="border border-slate-300 p-2">Kontoverwaltung, Benachrichtigungen</td>
                  <td className="border border-slate-300 p-2">Art. 6 Abs. 1 lit. b DSGVO</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Passwort (verschlüsselt)</td>
                  <td className="border border-slate-300 p-2">Sichere Authentifizierung</td>
                  <td className="border border-slate-300 p-2">Art. 6 Abs. 1 lit. b DSGVO</td>
                </tr>
              </tbody>
            </table>

            <h4 className="text-lg font-semibold mt-4 mb-2">2.2 Im Profil (Onboarding)</h4>
            <table className="w-full border-collapse border border-slate-300 my-4">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-300 p-2 text-left">Datenkategorie</th>
                  <th className="border border-slate-300 p-2 text-left">Zweck</th>
                  <th className="border border-slate-300 p-2 text-left">Pflichtfeld</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-300 p-2">Vorname, Nachname</td>
                  <td className="border border-slate-300 p-2">Identifikation bei Arbeitgebern</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Telefonnummer</td>
                  <td className="border border-slate-300 p-2">Kontaktaufnahme durch Arbeitgeber</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Profilbild</td>
                  <td className="border border-slate-300 p-2">Professionelle Präsentation</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Geburtsdatum</td>
                  <td className="border border-slate-300 p-2">Altersverifikation für Jobs</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Studiengang, Universität</td>
                  <td className="border border-slate-300 p-2">Passende Job-Empfehlungen</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Wohnort (Bundesland, Bezirk)</td>
                  <td className="border border-slate-300 p-2">Jobs in Ihrer Nähe finden</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Adresse</td>
                  <td className="border border-slate-300 p-2">Genauere Standortsuche</td>
                  <td className="border border-slate-300 p-2">Nein</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Mobilitätsoptionen</td>
                  <td className="border border-slate-300 p-2">Jobs mit Fahrerfordernis</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Sprachkenntnisse</td>
                  <td className="border border-slate-300 p-2">Jobs mit Sprachanforderungen</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Job-Interessen</td>
                  <td className="border border-slate-300 p-2">Passende Job-Kategorien</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Verfügbarkeit</td>
                  <td className="border border-slate-300 p-2">Jobs zu passenden Zeiten</td>
                  <td className="border border-slate-300 p-2">Ja</td>
                </tr>
              </tbody>
            </table>

            <h4 className="text-lg font-semibold mt-4 mb-2">2.3 Bei Bewerbungen</h4>
            <ul className="list-disc pl-6 my-4">
              <li>Ihre Profildaten werden dem Arbeitgeber zur Verfügung gestellt</li>
              <li>Bewerbungsstatus und -historie werden gespeichert</li>
              <li>Interne Notizen der Vermittler (nicht sichtbar für Arbeitgeber)</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">3. Datensicherheit</h3>
            <p>Wir schützen Ihre Daten durch:</p>
            <ul className="list-disc pl-6 my-4">
              <li><strong>SSL/TLS-Verschlüsselung:</strong> Alle Datenübertragungen sind verschlüsselt</li>
              <li><strong>Passwort-Hashing:</strong> Passwörter werden mit bcrypt sicher gespeichert</li>
              <li><strong>JWT-Authentifizierung:</strong> Sichere Token-basierte Anmeldung</li>
              <li><strong>Verschlüsselte SMTP-Zugangsdaten:</strong> E-Mail-Konfiguration ist verschlüsselt</li>
              <li><strong>Automatische Kontolöschung:</strong> Nicht verifizierte Konten werden nach 7 Tagen gelöscht</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">4. Speicherdauer</h3>
            <table className="w-full border-collapse border border-slate-300 my-4">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border border-slate-300 p-2 text-left">Daten</th>
                  <th className="border border-slate-300 p-2 text-left">Speicherdauer</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-300 p-2">Profildaten</td>
                  <td className="border border-slate-300 p-2">Bis zur Kontolöschung</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Nicht verifizierte Konten</td>
                  <td className="border border-slate-300 p-2">7 Tage, dann automatische Löschung</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Bewerbungen</td>
                  <td className="border border-slate-300 p-2">Bis zur Kontolöschung oder Rückzug</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2">Nach Kontolöschung</td>
                  <td className="border border-slate-300 p-2">30 Tage (Backup), dann endgültig gelöscht</td>
                </tr>
              </tbody>
            </table>

            <h3 className="text-xl font-semibold mt-6 mb-3">5. Weitergabe von Daten</h3>
            <p>Ihre Daten werden nur weitergegeben an:</p>
            <ul className="list-disc pl-6 my-4">
              <li><strong>Potenzielle Arbeitgeber:</strong> Nur bei aktiver Bewerbung (relevante Profildaten)</li>
              <li><strong>Verein Zoro Mitarbeiter:</strong> Für die Vermittlungsbetreuung</li>
              <li><strong>Technische Dienstleister:</strong> Hosting (mit Auftragsverarbeitungsvertrag)</li>
            </ul>
            <p className="text-green-700 bg-green-50 p-4 rounded-lg">
              <strong>Keine Datenweitergabe an Dritte zu Werbezwecken!</strong>
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">6. Ihre Rechte (DSGVO)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-semibold mb-2">Auskunftsrecht (Art. 15)</h4>
                <p className="text-sm text-slate-600">Sie können jederzeit Auskunft über Ihre gespeicherten Daten verlangen.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-semibold mb-2">Berichtigungsrecht (Art. 16)</h4>
                <p className="text-sm text-slate-600">Sie können fehlerhafte Daten korrigieren lassen.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-semibold mb-2">Löschungsrecht (Art. 17)</h4>
                <p className="text-sm text-slate-600">Sie können die Löschung Ihrer Daten verlangen.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-semibold mb-2">Einschränkungsrecht (Art. 18)</h4>
                <p className="text-sm text-slate-600">Sie können die Verarbeitung einschränken lassen.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-semibold mb-2">Datenportabilität (Art. 20)</h4>
                <p className="text-sm text-slate-600">Sie können Ihre Daten in maschinenlesbarem Format erhalten.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <h4 className="font-semibold mb-2">Widerspruchsrecht (Art. 21)</h4>
                <p className="text-sm text-slate-600">Sie können der Verarbeitung widersprechen.</p>
              </div>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3">7. Cookies</h3>
            <p>
              Informationen zu unseren Cookies finden Sie in unserer separaten Cookie-Richtlinie:
            </p>
            <Button
              onClick={() => navigate('/cookies')}
              variant="outline"
              className="rounded-full mt-2"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Zur Cookie-Richtlinie
            </Button>

            <h3 className="text-xl font-semibold mt-6 mb-3">8. Kontolöschung</h3>
            <p>
              Sie können Ihr Konto jederzeit selbst löschen über: <strong>Dashboard → Profil bearbeiten → Konto löschen</strong>
            </p>
            <p>
              Bei der Kontolöschung werden alle Ihre Daten (Profil, Bewerbungen, Dokumente) 
              innerhalb von 30 Tagen vollständig und unwiderruflich gelöscht.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">9. Beschwerderecht</h3>
            <p>
              Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren:
            </p>
            <p>
              <strong>Österreichische Datenschutzbehörde</strong><br />
              Barichgasse 40-42<br />
              1030 Wien<br />
              Telefon: +43 1 52 152-0<br />
              E-Mail: dsb@dsb.gv.at<br />
              Website: <a href="https://www.dsb.gv.at" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.dsb.gv.at</a>
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">10. Änderungen</h3>
            <p>
              Diese Datenschutzerklärung kann bei Bedarf aktualisiert werden. 
              Bei wesentlichen Änderungen werden registrierte Benutzer per E-Mail informiert.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
